import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { currentUrl } from "../../url"

export const customersApi = createApi({
  reducerPath: "customersApi",
  tagTypes: ["Customers", "ClientTransactions"],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: currentUrl,
    prepareHeaders: (headers) => {
      const accessToken = localStorage.getItem("accessToken")
      if (accessToken) {
        const token = accessToken.startsWith("Bearer ")
          ? accessToken
          : `Bearer ${accessToken}`
        headers.set("Authorization", token)
        headers.set("Accept", "application/json")
      }
      return headers
    },
  }),
  endpoints: (build) => ({
    getCustomers: build.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams()
        if (params.search && params.search.trim()) {
          searchParams.append("keyword", params.search.trim())
        }
        if (params.salonId) {
          searchParams.append("salon_id", params.salonId)
        }
        searchParams.append("page", params.page || 1)
        searchParams.append("perPage", params.perPage || 10)
        searchParams.append("limit", params.limit || params.perPage || 10)
        return `/master/search/clients?${searchParams.toString()}`
      },
      providesTags: (result) => {
        if (result && result.data && result.data.length > 0) {
          return [
            ...result.data.map(({ id }) => ({ type: "Customers", id })),
            { type: "Customers", id: "LIST" },
          ]
        }
        return [{ type: "Customers", id: "LIST" }]
      },
    }),

    updateCustomer: build.mutation({
      query: ({ id, ...data }) => ({
        url: `/master/clients/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: () => [{ type: "Customers", id: "LIST" }],
    }),

    // ── Client Transactions ──────────────────────────────────────────────
    getClientTransactions: build.query({
      query: ({ clientId, page = 1, perPage = 10 }) => {
        const params = new URLSearchParams()
        params.append("page", page)
        params.append("per_page", perPage)
        return `/master/clients/${clientId}/transactions?${params.toString()}`
      },
      providesTags: (result, error, { clientId }) =>
        result
          ? [
              ...(result.data || []).map(({ id }) => ({
                type: "ClientTransactions",
                id,
              })),
              { type: "ClientTransactions", id: `LIST-${clientId}` },
            ]
          : [{ type: "ClientTransactions", id: `LIST-${clientId}` }],
    }),

    createClientTransaction: build.mutation({
      query: ({ clientId, ...body }) => ({
        url: `/master/clients/${clientId}/transactions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { clientId }) => [
        { type: "ClientTransactions", id: `LIST-${clientId}` },
        { type: "Customers", id: "LIST" },
      ],
    }),

    deleteClientTransaction: build.mutation({
      query: ({ clientId, transactionId }) => ({
        url: `/master/clients/${clientId}/transactions/${transactionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { clientId }) => [
        { type: "ClientTransactions", id: `LIST-${clientId}` },
        { type: "Customers", id: "LIST" },
      ],
    }),
  }),
})

export const {
  useGetCustomersQuery,
  useUpdateCustomerMutation,
  useGetClientTransactionsQuery,
  useCreateClientTransactionMutation,
  useDeleteClientTransactionMutation,
} = customersApi
